import { useEffect, useId, useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ChevronRight, ClipboardCheck, Paperclip, AlertCircle, FileText, Trash2 } from 'lucide-react';
import { api, type UploadCustomerPoDocumentResponse } from '@/lib/api';
import { DocumentLink } from '@/components/document-link';

// ----- File upload constants (must match server caps in routes/rfqs.ts) -----
const ACCEPT = 'application/pdf,image/jpeg,image/png';
const MAX_BYTES = 10 * 1024 * 1024;
const ALLOWED_MIMES = new Set(['application/pdf', 'image/jpeg', 'image/png']);

interface Props {
  /** RFQ public id (rfq_…) — used in API paths and cache invalidation. */
  rfqPublicId: string;
  /** Customer's PO number (broker captures verbatim from their PO doc). */
  customerPoNumber: string | null;
  /** When the PO was received — auto-set on first capture, broker-editable. */
  customerPoReceivedAt: string | null;
  /** R2 object key of the uploaded PO doc — opened via a signed URL. */
  customerPoDocumentKey: string | null;
  /** Legacy public R2 URL (rows predating the private-bucket switch). */
  customerPoDocumentUrl: string | null;
  /** Original filename of the uploaded PO doc. */
  customerPoDocumentFilename: string | null;
  /** SHA-256 hex of the doc bytes — tooltipped for audit/tamper-evidence. */
  customerPoDocumentSha256: string | null;
  /** Free-text broker notes scoped to this PO (e.g., "PO via fax 5/20"). */
  customerPoNotes: string | null;
}

function fileToBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      const r = reader.result;
      if (typeof r !== 'string') return reject(new Error('Unexpected reader result'));
      const i = r.indexOf(',');
      resolve(i >= 0 ? r.slice(i + 1) : r);
    };
    reader.onerror = () => reject(reader.error ?? new Error('File read failed'));
    reader.readAsDataURL(file);
  });
}

/** YYYY-MM-DD format for <input type="date"> from an ISO timestamp. */
function isoToDateInput(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Format as local-date (broker is single-user, no tz mismatch concerns here).
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Local-date back to a midnight-UTC ISO for the PATCH body. */
function dateInputToIso(s: string): string | null {
  if (!s) return null;
  // Stored as a UTC midnight to avoid TZ rounding on display.
  return new Date(`${s}T00:00:00.000Z`).toISOString();
}

/**
 * Customer PO capture card — Step 3 first-class event.
 *
 * Sits on the RFQ detail above the line-items list because receiving the
 * customer's PO is the gate event before proforma issuance. Closed by default:
 * the one-line header carries what matters (status, PO number, received date,
 * whether a document is attached) and an Add PO / Edit button, so the form only
 * takes room when someone is actually filling it in.
 * Captures the PO# verbatim, the received date, an optional uploaded PO doc
 * (PDF/JPG/PNG, 10 MB cap, SHA-256 hashed server-side), and broker notes.
 */
export function CustomerPoCard({
  rfqPublicId,
  customerPoNumber,
  customerPoReceivedAt,
  customerPoDocumentKey,
  customerPoDocumentUrl,
  customerPoDocumentFilename,
  customerPoDocumentSha256,
  customerPoNotes,
}: Props) {
  const { getToken } = useAuth();
  const qc = useQueryClient();
  const inputRef = useRef<HTMLInputElement | null>(null);

  const hasPo = !!customerPoNumber;
  const hasDoc = !!customerPoDocumentKey || !!customerPoDocumentUrl;

  // `open` is the disclosure (closed by default); `editing` is what an open card shows:
  // the form when there is no PO yet or Edit was pressed, the read-only summary otherwise.
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(!hasPo);
  const bodyId = useId();
  const poInputRef = useRef<HTMLInputElement | null>(null);
  const focusPoNext = useRef(false);

  // Add PO / Edit open the form and put the cursor in the PO number field.
  useEffect(() => {
    if (open && editing && focusPoNext.current) {
      poInputRef.current?.focus();
      focusPoNext.current = false;
    }
  }, [open, editing]);
  function openForm() {
    focusPoNext.current = true;
    setEditing(true);
    setOpen(true);
  }

  const [poNumber, setPoNumber] = useState(customerPoNumber ?? '');
  const [receivedDate, setReceivedDate] = useState(isoToDateInput(customerPoReceivedAt));
  const [notes, setNotes] = useState(customerPoNotes ?? '');
  const [localError, setLocalError] = useState<string | null>(null);

  // When the underlying RFQ data changes (e.g., after a successful PATCH),
  // resync the form so we don't display stale values after the invalidation.
  useEffect(() => {
    setPoNumber(customerPoNumber ?? '');
    setReceivedDate(isoToDateInput(customerPoReceivedAt));
    setNotes(customerPoNotes ?? '');
  }, [customerPoNumber, customerPoReceivedAt, customerPoNotes]);

  const patch = useMutation({
    mutationFn: (body: {
      customerPoNumber?: string | null;
      customerPoReceivedAt?: string | null;
      customerPoNotes?: string | null;
    }) =>
      api(`/api/rfqs/${rfqPublicId}`, {
        method: 'PATCH',
        tokenGetter: () => getToken(),
        body: JSON.stringify(body),
      }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq', rfqPublicId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    },
  });

  const upload = useMutation({
    mutationFn: async (file: File) => {
      const fileBase64 = await fileToBase64(file);
      return api<UploadCustomerPoDocumentResponse>(
        `/api/rfqs/${rfqPublicId}/customer-po-document`,
        {
          method: 'POST',
          tokenGetter: () => getToken(),
          body: JSON.stringify({ fileBase64, fileName: file.name, mimeType: file.type }),
        },
      );
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['rfq', rfqPublicId] });
      qc.invalidateQueries({ queryKey: ['rfqs'] });
    },
  });

  function handleSave() {
    setLocalError(null);
    const trimmed = poNumber.trim();
    if (!trimmed) {
      setLocalError('PO number is required.');
      return;
    }
    patch.mutate(
      {
        customerPoNumber: trimmed,
        customerPoReceivedAt: dateInputToIso(receivedDate),
        customerPoNotes: notes.trim() ? notes.trim() : null,
      },
      { onSuccess: () => { setEditing(false); setOpen(false); } },
    );
  }

  function handleClear() {
    if (!confirm('Clear the customer PO? The uploaded PO document stays on the RFQ for audit history.')) return;
    patch.mutate({ customerPoNumber: null }, { onSuccess: () => setEditing(true) });
  }

  function pickFile() {
    setLocalError(null);
    inputRef.current?.click();
  }
  function onPicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!ALLOWED_MIMES.has(file.type)) {
      setLocalError(`Unsupported file type "${file.type || 'unknown'}". Accepted: PDF, JPEG, PNG.`);
      return;
    }
    if (file.size > MAX_BYTES) {
      setLocalError(`File is ${(file.size / 1024 / 1024).toFixed(1)} MB — limit is 10 MB.`);
      return;
    }
    upload.mutate(file);
  }

  const patchErr = patch.isError ? (patch.error as { message?: string })?.message ?? 'Save failed' : null;
  const uploadErr = upload.isError ? (upload.error as { message?: string })?.message ?? 'Upload failed' : null;
  const errMsg = localError ?? patchErr ?? uploadErr;

  return (
    <div className="mt-4 border border-border rounded-md bg-surface">
      <div className="flex items-center gap-2 pr-3">
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="flex min-h-11 min-w-0 flex-1 items-center gap-2 rounded-md px-3 py-1.5 text-left hover:bg-muted/60 sm:min-h-10"
        >
          <ChevronRight className={`h-4 w-4 shrink-0 text-muted-foreground transition-transform ${open ? 'rotate-90' : ''}`} aria-hidden />
          <span className="min-w-0">
            <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs uppercase tracking-wide text-muted-foreground">
              <ClipboardCheck className="h-3.5 w-3.5 shrink-0" aria-hidden />
              Customer PO
              {hasPo ? (
                <span className="rounded bg-emerald-100 px-1.5 py-0.5 text-[11px] font-medium normal-case tracking-normal text-emerald-800">
                  On file
                </span>
              ) : (
                <span className="rounded bg-amber-100 px-1.5 py-0.5 text-[11px] font-medium normal-case tracking-normal text-amber-800">
                  Awaiting PO
                </span>
              )}
            </span>
            {hasPo && (
              <span className="mt-0.5 flex items-center gap-1.5 text-xs text-foreground">
                <span className="truncate font-mono">{customerPoNumber}</span>
                {customerPoReceivedAt && (
                  <span className="shrink-0 text-muted-foreground">
                    · {new Date(customerPoReceivedAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                  </span>
                )}
                {hasDoc && <Paperclip className="h-3 w-3 shrink-0 text-emerald-700" aria-label="PO document attached" />}
              </span>
            )}
          </span>
        </button>
        {!(open && editing) && (
          <button
            type="button"
            onClick={openForm}
            className="inline-flex h-9 shrink-0 items-center rounded-md border border-border px-3 text-xs font-medium hover:bg-muted sm:h-8"
          >
            {hasPo ? 'Edit' : 'Add PO'}
          </button>
        )}
      </div>

      {open && (
      <div id={bodyId} className="space-y-3 border-t border-border px-4 py-3 text-sm">
        {editing ? (
          <div className="space-y-2.5">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              <label className="block">
                <span className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  PO number <span className="text-destructive">*</span>
                </span>
                <input
                  ref={poInputRef}
                  type="text"
                  value={poNumber}
                  onChange={(e) => setPoNumber(e.target.value)}
                  placeholder="e.g., 4500123987"
                  className="w-full px-2 py-1.5 border border-border rounded text-sm font-mono"
                  maxLength={200}
                />
              </label>
              <label className="block">
                <span className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                  Received date
                </span>
                <input
                  type="date"
                  value={receivedDate}
                  onChange={(e) => setReceivedDate(e.target.value)}
                  className="w-full px-2 py-1.5 border border-border rounded text-sm"
                />
                <span className="block text-[11px] text-muted-foreground mt-0.5">
                  Defaults to upload time if left blank.
                </span>
              </label>
            </div>
            <label className="block">
              <span className="block text-xs uppercase tracking-wide text-muted-foreground mb-1">
                PO notes
              </span>
              <textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., PO emailed by Mindy on 5/20; covers all 3 lines."
                rows={2}
                maxLength={2000}
                className="w-full px-2 py-1.5 border border-border rounded text-sm"
              />
            </label>
            <div className="flex items-center justify-between gap-2 pt-1">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={handleSave}
                  disabled={patch.isPending}
                  className="px-3 py-2 text-xs font-medium bg-primary text-primary-foreground rounded hover:opacity-90 disabled:opacity-50 sm:py-1"
                >
                  {patch.isPending ? 'Saving…' : hasPo ? 'Save changes' : 'Save PO'}
                </button>
                {hasPo && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(false);
                      setPoNumber(customerPoNumber ?? '');
                      setReceivedDate(isoToDateInput(customerPoReceivedAt));
                      setNotes(customerPoNotes ?? '');
                      setLocalError(null);
                    }}
                    className="px-3 py-2 text-xs border border-border rounded hover:bg-muted sm:px-2.5 sm:py-1"
                  >
                    Cancel
                  </button>
                )}
              </div>
              {hasPo && (
                <button
                  type="button"
                  onClick={handleClear}
                  disabled={patch.isPending}
                  className="inline-flex items-center gap-1 px-2 py-2 text-xs text-destructive sm:py-1 border border-border rounded hover:bg-destructive/10 disabled:opacity-50"
                  title="Clear PO# and received date. Uploaded document stays for audit."
                >
                  <Trash2 className="w-3 h-3" aria-hidden /> Clear PO
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">PO number</div>
              <div className="font-mono text-sm">{customerPoNumber}</div>
            </div>
            <div>
              <div className="text-xs uppercase tracking-wide text-muted-foreground">Received</div>
              <div className="text-sm">
                {customerPoReceivedAt
                  ? new Date(customerPoReceivedAt).toLocaleDateString()
                  : <span className="italic text-muted-foreground">—</span>}
              </div>
            </div>
            {customerPoNotes && (
              <div className="sm:col-span-2">
                <div className="text-xs uppercase tracking-wide text-muted-foreground">Notes</div>
                <div className="text-sm whitespace-pre-wrap">{customerPoNotes}</div>
              </div>
            )}
          </div>
        )}

        {/* PO document — always visible, both modes, so broker can attach mid-edit. */}
        <div className="pt-2 border-t border-border flex items-center flex-wrap gap-2 text-xs">
          {hasDoc ? (
            <>
              <FileText className="w-3.5 h-3.5 text-emerald-700 flex-shrink-0" aria-hidden />
              <DocumentLink
                objectKey={customerPoDocumentKey}
                fallbackUrl={customerPoDocumentUrl}
                label={customerPoDocumentFilename ?? 'Customer PO doc'}
                title={`${customerPoDocumentFilename ?? 'Customer PO doc'}${customerPoDocumentSha256 ? `\nSHA-256: ${customerPoDocumentSha256}` : ''}`}
                className="text-primary hover:underline font-medium truncate max-w-[18rem]"
              />
              <button
                type="button"
                onClick={pickFile}
                disabled={upload.isPending}
                className="px-2 py-1.5 border border-border rounded hover:bg-muted disabled:opacity-50 sm:px-1.5 sm:py-0.5"
              >
                {upload.isPending ? 'Uploading…' : 'Replace'}
              </button>
            </>
          ) : (
            <>
              <Paperclip className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" aria-hidden />
              <button
                type="button"
                onClick={pickFile}
                disabled={upload.isPending}
                className="px-2 py-1.5 border border-border rounded hover:bg-muted text-muted-foreground disabled:opacity-50 sm:px-1.5 sm:py-0.5"
              >
                {upload.isPending ? 'Uploading…' : '+ Attach customer PO (PDF/JPG/PNG)'}
              </button>
              <span className="text-[11px] text-muted-foreground italic">
                Up to 10 MB. Stored with SHA-256 for audit.
              </span>
            </>
          )}
        </div>

        {errMsg && (
          <div className="flex items-center gap-1 text-xs text-destructive">
            <AlertCircle className="w-3.5 h-3.5 flex-shrink-0" /> {errMsg}
          </div>
        )}

        <input
          ref={inputRef}
          type="file"
          accept={ACCEPT}
          onChange={onPicked}
          className="hidden"
        />
      </div>
      )}
    </div>
  );
}
